<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import ClosureAPI from 'dashboard/api/closure';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// SICARG MOD 3 — Estadísticas de cierres (admin).
const { t } = useI18n();
const router = useRouter();
const store = useStore();
const { accountScopedRoute } = useAccount();

const isLoading = ref(false);
const since = ref('');
const until = ref('');
// SICARG MOD 3 v2 — Filtros por agente / motivo / estado de pago.
const selectedAgent = ref('');
const selectedReason = ref('');
const selectedPaymentStatus = ref('');
const reasons = ref([]);
const paymentStatuses = ref([]);
const agents = useMapGetter('agents/getAgents');
const emptyStats = () => ({
  total: 0,
  total_managed_amount: 0,
  average_managed_amount: 0,
  average_handling_seconds: 0,
  by_reason: {},
  by_sub_reason: {},
  by_payment_status: {},
  by_agent: [],
  amount_by_agent: [],
  rates: { conversion_paid: 0, promise_rate: 0, contact_effective: 0 },
  funnel: { contacted: 0, promised: 0, paid: 0 },
  promise_fulfillment: { total: 0, fulfilled: 0, rate: 0 },
});

const stats = ref(emptyStats());

const load = async () => {
  isLoading.value = true;
  try {
    const params = {};
    if (since.value) params.since = since.value;
    if (until.value) params.until = until.value;
    if (selectedAgent.value) params.user_id = selectedAgent.value;
    if (selectedReason.value) params.closure_reason = selectedReason.value;
    if (selectedPaymentStatus.value) {
      params.payment_status = selectedPaymentStatus.value;
    }
    const { data } = await ClosureAPI.getStatistics(params);
    stats.value = { ...emptyStats(), ...data };
  } catch (error) {
    useAlert(t('CLOSURE_STATISTICS.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const loadFilterOptions = async () => {
  store.dispatch('agents/get');
  try {
    const { data } = await ClosureAPI.getSettings();
    reasons.value = data.reasons || [];
    paymentStatuses.value = data.payment_statuses || [];
  } catch (error) {
    reasons.value = [];
    paymentStatuses.value = [];
  }
};

const resetFilters = () => {
  since.value = '';
  until.value = '';
  selectedAgent.value = '';
  selectedReason.value = '';
  selectedPaymentStatus.value = '';
  load();
};

onMounted(() => {
  loadFilterOptions();
  load();
});

const goToConfig = () => {
  router.push(accountScopedRoute('closure_settings_index'));
};

const goToPanel = () => {
  router.push(accountScopedRoute('closure_panel_index'));
};

const entries = obj => Object.entries(obj || {});

const asPercent = value => `${Math.round((value || 0) * 100)}%`;

// Plata → "$1.234,56"
const formatMoney = value => {
  const number = Number(value || 0);
  return `$${number.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Segundos → "Xh Ym" legible.
const formatDuration = seconds => {
  const total = Math.max(0, Math.round(seconds || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

// Estilo de los <select> para que matcheen el alto/estilo de NextInput (h-10).
// !h-10/!min-h-0 fuerzan la altura por encima de estilos globales de Chatwoot.
const filterSelectClass =
  'w-full !h-10 !min-h-0 !m-0 px-3 text-sm leading-tight rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak border-none';
</script>

<template>
  <SettingsLayout :no-records-found="false" class="gap-6">
    <template #header>
      <BaseSettingsHeader
        :title="t('CLOSURE_STATISTICS.HEADER.TITLE')"
        :description="t('CLOSURE_STATISTICS.HEADER.DESCRIPTION')"
      >
        <template #actions>
          <NextButton
            faded
            slate
            sm
            icon="i-lucide-layout-dashboard"
            :label="t('CLOSURE_STATISTICS.PANEL_LINK')"
            @click="goToPanel"
          />
          <NextButton
            faded
            slate
            sm
            icon="i-lucide-settings-2"
            :label="t('CLOSURE_STATISTICS.CONFIG_LINK')"
            @click="goToConfig"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-6 mt-4">
        <!-- Filtros: grilla de 2 filas (etiquetas arriba, controles abajo) para
             alinear todo sin importar diferencias de alto entre inputs y selects. -->
        <div class="flex flex-wrap gap-3 items-end">
          <div
            class="grid gap-x-3 gap-y-1.5 items-end"
            :style="{
              gridAutoFlow: 'column',
              gridTemplateRows: 'auto auto',
              gridAutoColumns: '10rem',
            }"
          >
            <label class="text-xs text-n-slate-11">{{
              t('CLOSURE_STATISTICS.FILTER.SINCE')
            }}</label>
            <NextInput v-model="since" type="date" />

            <label class="text-xs text-n-slate-11">{{
              t('CLOSURE_STATISTICS.FILTER.UNTIL')
            }}</label>
            <NextInput v-model="until" type="date" />

            <label class="text-xs text-n-slate-11">{{
              t('CLOSURE_STATISTICS.FILTER.AGENT')
            }}</label>
            <select v-model="selectedAgent" :class="filterSelectClass">
              <option value="">{{ t('CLOSURE_STATISTICS.FILTER.ALL') }}</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>

            <label class="text-xs text-n-slate-11">{{
              t('CLOSURE_STATISTICS.FILTER.REASON')
            }}</label>
            <select v-model="selectedReason" :class="filterSelectClass">
              <option value="">{{ t('CLOSURE_STATISTICS.FILTER.ALL') }}</option>
              <option
                v-for="reason in reasons"
                :key="reason.value"
                :value="reason.value"
              >
                {{ reason.label }}
              </option>
            </select>

            <label class="text-xs text-n-slate-11">{{
              t('CLOSURE_STATISTICS.FILTER.PAYMENT_STATUS')
            }}</label>
            <select v-model="selectedPaymentStatus" :class="filterSelectClass">
              <option value="">{{ t('CLOSURE_STATISTICS.FILTER.ALL') }}</option>
              <option
                v-for="status in paymentStatuses"
                :key="status.value"
                :value="status.value"
              >
                {{ status.label }}
              </option>
            </select>
          </div>

          <div class="flex gap-2 items-end">
            <NextButton
              blue
              sm
              :is-loading="isLoading"
              :label="t('CLOSURE_STATISTICS.FILTER.APPLY')"
              @click="load"
            />
            <NextButton
              faded
              slate
              sm
              :label="t('CLOSURE_STATISTICS.FILTER.RESET')"
              @click="resetFilters"
            />
          </div>
        </div>

        <!-- Totales -->
        <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div class="p-4 rounded-lg border border-n-weak">
            <p class="text-sm text-n-slate-11">
              {{ t('CLOSURE_STATISTICS.TOTALS.COUNT') }}
            </p>
            <p class="text-2xl font-semibold text-n-slate-12">
              {{ stats.total }}
            </p>
          </div>
          <div class="p-4 rounded-lg border border-n-weak">
            <p class="text-sm text-n-slate-11">
              {{ t('CLOSURE_STATISTICS.TOTALS.MANAGED_AMOUNT') }}
            </p>
            <p class="text-2xl font-semibold text-n-slate-12">
              {{ formatMoney(stats.total_managed_amount) }}
            </p>
          </div>
          <div class="p-4 rounded-lg border border-n-weak">
            <p class="text-sm text-n-slate-11">
              {{ t('CLOSURE_STATISTICS.TOTALS.AVERAGE_AMOUNT') }}
            </p>
            <p class="text-2xl font-semibold text-n-slate-12">
              {{ formatMoney(stats.average_managed_amount) }}
            </p>
          </div>
          <div class="p-4 rounded-lg border border-n-weak">
            <p class="text-sm text-n-slate-11">
              {{ t('CLOSURE_STATISTICS.TOTALS.AVG_HANDLING') }}
            </p>
            <p class="text-2xl font-semibold text-n-slate-12">
              {{ formatDuration(stats.average_handling_seconds) }}
            </p>
          </div>
        </div>

        <!-- Tasas clave -->
        <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div class="p-4 rounded-lg border border-n-weak">
            <p class="text-sm text-n-slate-11">
              {{ t('CLOSURE_STATISTICS.RATES.CONVERSION') }}
            </p>
            <p class="text-2xl font-semibold text-n-slate-12">
              {{ asPercent(stats.rates.conversion_paid) }}
            </p>
          </div>
          <div class="p-4 rounded-lg border border-n-weak">
            <p class="text-sm text-n-slate-11">
              {{ t('CLOSURE_STATISTICS.RATES.PROMISE') }}
            </p>
            <p class="text-2xl font-semibold text-n-slate-12">
              {{ asPercent(stats.rates.promise_rate) }}
            </p>
          </div>
          <div class="p-4 rounded-lg border border-n-weak">
            <p class="text-sm text-n-slate-11">
              {{ t('CLOSURE_STATISTICS.RATES.CONTACT_EFFECTIVE') }}
            </p>
            <p class="text-2xl font-semibold text-n-slate-12">
              {{ asPercent(stats.rates.contact_effective) }}
            </p>
          </div>
          <div class="p-4 rounded-lg border border-n-weak">
            <p class="text-sm text-n-slate-11">
              {{ t('CLOSURE_STATISTICS.RATES.PROMISE_FULFILLMENT') }}
            </p>
            <p class="text-2xl font-semibold text-n-slate-12">
              {{ asPercent(stats.promise_fulfillment.rate) }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ stats.promise_fulfillment.fulfilled }} /
              {{ stats.promise_fulfillment.total }}
            </p>
          </div>
        </div>

        <!-- Funnel -->
        <div class="p-4 rounded-lg border border-n-weak">
          <p class="mb-3 text-sm font-medium text-n-slate-12">
            {{ t('CLOSURE_STATISTICS.FUNNEL.TITLE') }}
          </p>
          <div class="grid grid-cols-3 gap-4 text-center">
            <div>
              <p class="text-xl font-semibold text-n-slate-12">
                {{ stats.funnel.contacted }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ t('CLOSURE_STATISTICS.FUNNEL.CONTACTED') }}
              </p>
            </div>
            <div>
              <p class="text-xl font-semibold text-n-slate-12">
                {{ stats.funnel.promised }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ t('CLOSURE_STATISTICS.FUNNEL.PROMISED') }}
              </p>
            </div>
            <div>
              <p class="text-xl font-semibold text-n-slate-12">
                {{ stats.funnel.paid }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ t('CLOSURE_STATISTICS.FUNNEL.PAID') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Por motivo -->
        <div class="p-4 rounded-lg border border-n-weak">
          <p class="mb-3 text-sm font-medium text-n-slate-12">
            {{ t('CLOSURE_STATISTICS.BY_REASON') }}
          </p>
          <div
            v-for="[key, value] in entries(stats.by_reason)"
            :key="`reason-${key}`"
            class="flex justify-between py-1 text-sm border-b border-n-weak/40"
          >
            <span class="text-n-slate-11">{{ key }}</span>
            <span class="font-medium text-n-slate-12">{{ value }}</span>
          </div>
        </div>

        <!-- Por submotivo -->
        <div class="p-4 rounded-lg border border-n-weak">
          <p class="mb-3 text-sm font-medium text-n-slate-12">
            {{ t('CLOSURE_STATISTICS.BY_SUB_REASON') }}
          </p>
          <div
            v-for="[key, value] in entries(stats.by_sub_reason)"
            :key="`sub-${key}`"
            class="flex justify-between py-1 text-sm border-b border-n-weak/40"
          >
            <span class="text-n-slate-11">{{ key }}</span>
            <span class="font-medium text-n-slate-12">{{ value }}</span>
          </div>
        </div>

        <!-- Por estado de pago -->
        <div class="p-4 rounded-lg border border-n-weak">
          <p class="mb-3 text-sm font-medium text-n-slate-12">
            {{ t('CLOSURE_STATISTICS.BY_PAYMENT_STATUS') }}
          </p>
          <div
            v-for="[key, value] in entries(stats.by_payment_status)"
            :key="`ps-${key}`"
            class="flex justify-between py-1 text-sm border-b border-n-weak/40"
          >
            <span class="text-n-slate-11">{{ key }}</span>
            <span class="font-medium text-n-slate-12">{{ value }}</span>
          </div>
        </div>

        <!-- Por agente -->
        <div class="p-4 rounded-lg border border-n-weak">
          <p class="mb-3 text-sm font-medium text-n-slate-12">
            {{ t('CLOSURE_STATISTICS.BY_AGENT') }}
          </p>
          <div
            v-for="agent in stats.by_agent"
            :key="`agent-${agent.agent_id}`"
            class="flex justify-between py-1 text-sm border-b border-n-weak/40"
          >
            <span class="text-n-slate-11">{{ agent.agent_name }}</span>
            <span class="font-medium text-n-slate-12">{{ agent.count }}</span>
          </div>
        </div>

        <!-- Ranking de agentes por monto gestionado -->
        <div class="p-4 rounded-lg border border-n-weak">
          <p class="mb-3 text-sm font-medium text-n-slate-12">
            {{ t('CLOSURE_STATISTICS.AMOUNT_BY_AGENT') }}
          </p>
          <div
            v-for="agent in stats.amount_by_agent"
            :key="`amount-agent-${agent.agent_id}`"
            class="flex justify-between py-1 text-sm border-b border-n-weak/40"
          >
            <span class="text-n-slate-11">{{ agent.agent_name }}</span>
            <span class="font-medium text-n-slate-12">{{
              formatMoney(agent.amount)
            }}</span>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
